import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "@/lib/api";
import { dateTime } from "@/lib/format";
import type { CarrierWebhookEvent, CarrierWebhookIntegration, PlatformWebhookDestination } from "@/lib/types";
import { Alert, Badge, Button, Card, Field, PageHeader, Skeleton, Table } from "@/components/ui";

interface CreatedIntegration {
  integration: CarrierWebhookIntegration;
  webhook_url: string;
  notice: string;
}

interface CreatedDestination {
  destination: PlatformWebhookDestination;
  secret: string;
  notice: string;
}

/**
 * Inbound webhooks from WeShip's own ShipLogic account, and where every one
 * of them gets relayed on to.
 *
 * The receiving URL's token is the only credential ShipLogic presents -- it
 * has no session to sign a request with -- so it follows the same
 * shown-once rule as any other secret on this platform: paste it into
 * ShipLogic's own webhook settings immediately, because this screen will
 * never show it again.
 */
export function AdminWebhooks() {
  return (
    <>
      <PageHeader
        title="Webhooks"
        subtitle="What WeShip's own ShipLogic account calls back with, and where it gets relayed on to."
      />
      <CarrierIntegrations />
      <RelayDestinations />
      <RecentEvents />
    </>
  );
}

function CarrierIntegrations() {
  const queryClient = useQueryClient();
  const [label, setLabel] = useState("");
  const [created, setCreated] = useState<CreatedIntegration | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["carrier-webhook-integrations"],
    queryFn: () => api.get<{ data: CarrierWebhookIntegration[] }>("/v1/admin/webhooks/shiplogic"),
  });

  const create = useMutation({
    mutationFn: () => api.post<CreatedIntegration>("/v1/admin/webhooks/shiplogic", { label }),
    onSuccess: (result) => {
      setCreated(result);
      setLabel("");
      queryClient.invalidateQueries({ queryKey: ["carrier-webhook-integrations"] });
    },
  });

  const setActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.patch(`/v1/admin/webhooks/shiplogic/${id}`, { active }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["carrier-webhook-integrations"] }),
  });

  return (
    <Card className="mb-6">
      <h2 className="text-lg font-bold tracking-tight">ShipLogic integrations</h2>
      <p className="mb-4 mt-1 text-sm text-ink-500">
        A receiving URL for ShipLogic to call. Paste it into ShipLogic's own webhook settings for WeShip's account.
      </p>

      {created && (
        <div className="mb-4">
          <Alert tone="warn">
            <p className="font-medium">{created.notice}</p>
            <code className="mt-2 block break-all rounded-lg bg-white px-3 py-2 font-mono text-xs text-ink-900">
              {created.webhook_url}
            </code>
          </Alert>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-[1fr_auto] sm:items-end">
        <Field label="Label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Primary ShipLogic account" />
        <Button loading={create.isPending} disabled={!label.trim()} onClick={() => create.mutate()}>
          Create integration
        </Button>
      </div>

      {isLoading ? (
        <Skeleton className="mt-4 h-24 w-full" />
      ) : !data?.data.length ? (
        <p className="mt-4 text-sm text-ink-500">No integrations yet.</p>
      ) : (
        <div className="mt-4 divide-y divide-ink-200 border-t border-ink-200">
          {data.data.map((integration) => (
            <div key={integration.id} className="flex flex-wrap items-center gap-3 py-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-medium">{integration.label}</p>
                  <Badge tone={integration.is_active ? "good" : "bad"}>
                    {integration.is_active ? "active" : "inactive"}
                  </Badge>
                </div>
                <p className="mt-0.5 text-xs text-ink-400">Created {dateTime(integration.created_at)}</p>
              </div>
              <Button
                size="sm"
                variant="secondary"
                loading={setActive.isPending}
                onClick={() => setActive.mutate({ id: integration.id, active: !integration.is_active })}
              >
                {integration.is_active ? "Deactivate" : "Activate"}
              </Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function RelayDestinations() {
  const queryClient = useQueryClient();
  const [label, setLabel] = useState("");
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");
  const [created, setCreated] = useState<CreatedDestination | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["webhook-destinations"],
    queryFn: () => api.get<{ data: PlatformWebhookDestination[] }>("/v1/admin/webhooks/destinations"),
  });

  const create = useMutation({
    mutationFn: () => api.post<CreatedDestination>("/v1/admin/webhooks/destinations", { label, url }),
    onMutate: () => setError(""),
    onSuccess: (result) => {
      setCreated(result);
      setLabel("");
      setUrl("");
      queryClient.invalidateQueries({ queryKey: ["webhook-destinations"] });
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : "Could not add that destination."),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.remove<void>(`/v1/admin/webhooks/destinations/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["webhook-destinations"] }),
  });

  return (
    <Card className="mb-6">
      <h2 className="text-lg font-bold tracking-tight">Relay destinations</h2>
      <p className="mb-4 mt-1 text-sm text-ink-500">
        Every inbound ShipLogic call is forwarded on to each of these, unchanged -- WeShip's own tooling, a partner,
        anywhere else it needs to go.
      </p>

      {created && (
        <div className="mb-4">
          <Alert tone="warn">
            <p className="font-medium">{created.notice}</p>
            <code className="mt-2 block break-all rounded-lg bg-white px-3 py-2 font-mono text-xs text-ink-900">
              {created.secret}
            </code>
          </Alert>
        </div>
      )}
      {error && (
        <div className="mb-4">
          <Alert>{error}</Alert>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
        <Field label="Label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="WeShip ops tooling" />
        <Field label="URL" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://ops.example.com/inbound" />
        <Button loading={create.isPending} disabled={!label.trim() || !url.trim()} onClick={() => create.mutate()}>
          Add destination
        </Button>
      </div>

      {isLoading ? (
        <Skeleton className="mt-4 h-24 w-full" />
      ) : !data?.data.length ? (
        <p className="mt-4 text-sm text-ink-500">No relay destinations yet.</p>
      ) : (
        <div className="mt-4 divide-y divide-ink-200 border-t border-ink-200">
          {data.data.map((destination) => (
            <div key={destination.id} className="flex flex-wrap items-center gap-3 py-3">
              <div className="min-w-0 flex-1">
                <p className="font-medium">{destination.label}</p>
                <p className="truncate font-mono text-xs text-ink-400">{destination.url}</p>
              </div>
              <Button size="sm" variant="danger" onClick={() => remove.mutate(destination.id)}>
                Remove
              </Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function RecentEvents() {
  const { data, isLoading } = useQuery({
    queryKey: ["carrier-webhook-events"],
    queryFn: () => api.get<{ data: CarrierWebhookEvent[] }>("/v1/admin/webhooks/shiplogic/events"),
  });

  return (
    <Card>
      <h2 className="text-lg font-bold tracking-tight">Recent events</h2>
      <p className="mb-4 mt-1 text-sm text-ink-500">
        Every inbound call, whether or not this platform could make sense of it. An event that failed to parse is
        kept here with its raw body, not dropped.
      </p>

      {isLoading || !data ? (
        <Skeleton className="h-40 w-full" />
      ) : data.data.length === 0 ? (
        <p className="text-sm text-ink-500">Nothing received yet.</p>
      ) : (
        <Table head={["When", "Status", "Shipment", "Outcome"]}>
          {data.data.map((event) => (
            <tr key={event.id}>
              <td className="ws-td whitespace-nowrap text-ink-500">{dateTime(event.received_at)}</td>
              <td className="ws-td">{event.status || "-"}</td>
              <td className="ws-td font-mono text-xs">{event.shipment_id || "-"}</td>
              <td className="ws-td">
                {event.parsed ? (
                  <Badge tone="good">applied</Badge>
                ) : (
                  <>
                    <Badge tone="bad">not parsed</Badge>
                    {event.error && <p className="mt-1 max-w-sm text-xs text-ink-400">{event.error}</p>}
                  </>
                )}
              </td>
            </tr>
          ))}
        </Table>
      )}
    </Card>
  );
}
